/**
 * Wallet Selection Step
 *
 * First step in the wallet modal - choose wallet type or select existing
 * Bank-grade UX with EverReal brand design system
 */

import {
  Fingerprint,
  HardDrive,
  Download,
  Plus,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { motion } from "framer-motion";
import { AnimatedCard, PulsingCard } from "@/components/brand/AnimatedCard";
import {
  useBrowserWallet,
  useBrowserWalletStore,
  useWalletContext,
} from "@nullaone/wallet-react";
import {
  isPRFSupported,
  MAX_LOCAL_WALLETS,
  MAX_LEDGER_WALLETS,
} from "@nullaone/wallet-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SearchInput } from "@/components/brand/BrandInput";

// Animation variants for staggered entry
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" as const },
  },
};

export function WalletSelectStep() {
  const { t } = useTranslation();
  const { storedWallets, selectWallet, connecting, closeModal } =
    useBrowserWallet();
  const { setModalStep } = useBrowserWalletStore();
  const { core } = useWalletContext();
  const [prfSupported, setPrfSupported] = useState(true);
  const [walletCounts, setWalletCounts] = useState({ local: 0, ledger: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const webHIDSupported = core.ledger.isWebHIDSupported();

  const showSearch = storedWallets.length >= 7;

  const filteredWallets = useMemo(() => {
    if (!searchQuery.trim()) return storedWallets;
    const q = searchQuery.toLowerCase();
    return storedWallets.filter(
      (w) =>
        w.name?.toLowerCase().includes(q) ||
        w.address.toLowerCase().includes(q),
    );
  }, [storedWallets, searchQuery]);

  const skipStagger = storedWallets.length > 10;

  const updateScrollIndicators = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    isPRFSupported().then(setPrfSupported);
  }, []);

  // Refresh wallet counts when stored wallets change
  useEffect(() => {
    core.storage.getWalletCountByType().then(setWalletCounts);
  }, [storedWallets, core.storage]);

  // Update scroll indicator when wallet list changes
  useEffect(() => {
    requestAnimationFrame(updateScrollIndicators);
  }, [storedWallets.length, searchQuery, updateScrollIndicators]);

  const handleSelectWallet = async (address: string) => {
    try {
      await selectWallet(address);
      closeModal(); // Close modal to trigger signature flow in useAuthModal
    } catch {
      // Error handled by hook
    }
  };

  // Check wallet limits
  const canCreateLocal = walletCounts.local < MAX_LOCAL_WALLETS;
  const canConnectLedger = walletCounts.ledger < MAX_LEDGER_WALLETS;

  return (
    <motion.div
      className="space-y-6 py-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Existing wallets — scrollable list with search */}
      {storedWallets.length > 0 && (
        <motion.div className="space-y-3" variants={itemVariants}>
          <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide">
            {t("wallet.select.yourWallets")}
          </h3>
          {showSearch && (
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("wallet.select.searchPlaceholder")}
              variant="filled"
              inputSize="sm"
            />
          )}
          <div className="relative">
            <div
              ref={scrollContainerRef}
              onScroll={updateScrollIndicators}
              className={cn(
                "space-y-2 overflow-y-auto wallet-scroll-container",
                showSearch ? "h-[300px]" : "max-h-[300px]",
              )}
            >
              {filteredWallets.map((wallet, index) => (
                <motion.div
                  key={wallet.id}
                  variants={skipStagger ? undefined : itemVariants}
                  custom={index}
                >
                  <AnimatedCard
                    variant="outlined"
                    enableLift
                    enableScale
                    glow="subtle"
                    onClick={
                      connecting
                        ? undefined
                        : () => handleSelectWallet(wallet.address)
                    }
                    className={
                      connecting ? "opacity-50 cursor-not-allowed" : ""
                    }
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Icon with gradient background */}
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 border border-blue-200 shrink-0">
                        {wallet.type === "ledger" ? (
                          <HardDrive className="h-6 w-6 text-brand-primary" />
                        ) : (
                          <Fingerprint className="h-6 w-6 text-brand-primary" />
                        )}
                      </div>

                      {/* Wallet info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text-primary truncate">
                          {wallet.name ||
                            t("wallet.select.walletFallbackName", {
                              prefix: wallet.address.slice(0, 8),
                            })}
                        </p>
                        <p className="text-sm text-text-muted font-mono">
                          {wallet.address.slice(0, 4)}...
                          {wallet.address.slice(-4)}
                        </p>
                      </div>

                      {/* Backup warning badge - clickable to open backup flow */}
                      {!wallet.backedUp && wallet.type === "local_webauthn" && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await selectWallet(wallet.address);
                              setModalStep("backup");
                            } catch {
                              // Error handled by hook
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10 border border-warning/30 shrink-0 hover:bg-warning/15 transition-colors cursor-pointer"
                        >
                          <AlertTriangle className="h-3 w-3 text-warning-dark" />
                          <span className="text-xs text-warning-dark font-medium">
                            {t("wallet.select.backupBadge")}
                          </span>
                        </button>
                      )}

                      {/* Chevron indicator */}
                      <ChevronRight className="h-5 w-5 text-text-muted group-hover:text-brand-primary transition-colors shrink-0" />
                    </div>
                  </AnimatedCard>
                </motion.div>
              ))}
              {filteredWallets.length === 0 && searchQuery && (
                <p className="text-sm text-text-muted text-center py-4">
                  {t("wallet.select.noMatch", { query: searchQuery })}
                </p>
              )}
            </div>
            {/* Fade gradient when more wallets below */}
            {canScrollDown && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/90 to-transparent rounded-b-lg" />
            )}
          </div>
        </motion.div>
      )}

      {/* Limit message when local wallet limit reached */}
      {!canCreateLocal && storedWallets.length > 0 && (
        <motion.p
          variants={itemVariants}
          className="text-xs text-warning-dark bg-warning/10 border border-warning/30 px-3 py-2 rounded-lg"
        >
          {t("wallet.select.maxLocalReached", { max: MAX_LOCAL_WALLETS })}
        </motion.p>
      )}

      {/* Create/Import options - only show if under limit */}
      {canCreateLocal && (
        <motion.div className="space-y-3" variants={itemVariants}>
          <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide">
            {t("wallet.select.getStarted")}
          </h3>

          <div className="grid gap-3">
            {/* Create New Wallet - Primary Action with Pulsing Effect */}
            <motion.div variants={itemVariants}>
              {prfSupported && !connecting ? (
                <PulsingCard
                  variant="premium"
                  pulseSpeed={2.5}
                  className="cursor-pointer"
                  onClick={() => setModalStep("create")}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Gold gradient icon */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-light shadow-lg shadow-brand-primary/30 shrink-0">
                      <Plus className="h-6 w-6 text-white" />
                    </div>

                    <div className="flex-1">
                      <p className="font-semibold text-text-primary">
                        {t("wallet.select.create.title")}
                      </p>
                      <p className="text-sm text-text-muted">
                        {t("wallet.select.create.desc")}
                      </p>
                    </div>

                    <ChevronRight className="h-5 w-5 text-brand-primary shrink-0" />
                  </div>
                </PulsingCard>
              ) : (
                <AnimatedCard
                  variant="glass"
                  enableLift={false}
                  enableScale={false}
                  className="opacity-50 cursor-not-allowed"
                >
                  <div className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 border border-border-card shrink-0">
                      <Plus className="h-6 w-6 text-text-muted" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-text-muted">
                        {t("wallet.select.create.title")}
                      </p>
                      <p className="text-sm text-text-secondary">
                        {t("wallet.select.create.desc")}
                      </p>
                    </div>
                  </div>
                </AnimatedCard>
              )}
            </motion.div>

            {!prfSupported && (
              <motion.p
                variants={itemVariants}
                className="text-xs text-warning-dark bg-warning/10 border border-warning/30 px-3 py-2 rounded-lg"
              >
                {t("wallet.select.prfUnsupported")}
              </motion.p>
            )}

            {/* Import Existing Wallet */}
            <motion.div variants={itemVariants}>
              <AnimatedCard
                variant="glass"
                enableLift
                glow="subtle"
                onClick={
                  !prfSupported || connecting
                    ? undefined
                    : () => setModalStep("import")
                }
                className={
                  !prfSupported || connecting
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 border border-border-card shrink-0">
                    <Download className="h-6 w-6 text-text-muted" />
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-text-primary">
                      {t("wallet.select.import.title")}
                    </p>
                    <p className="text-sm text-text-muted">
                      {t("wallet.select.import.desc")}
                    </p>
                  </div>

                  <ChevronRight className="h-5 w-5 text-text-muted shrink-0" />
                </div>
              </AnimatedCard>
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Ledger option - available if under limit */}
      <motion.div className="space-y-3" variants={itemVariants}>
        {!canCreateLocal && (
          <h3 className="text-sm font-medium text-text-muted uppercase tracking-wide">
            {t("wallet.select.hardwareWalletHeading")}
          </h3>
        )}

        <div className="grid gap-3">
          {/* Ledger Connection */}
          {canConnectLedger ? (
            <motion.div variants={itemVariants}>
              <AnimatedCard
                variant="glass"
                enableLift
                glow="subtle"
                onClick={
                  !webHIDSupported || connecting
                    ? undefined
                    : () => setModalStep("ledger")
                }
                className={
                  !webHIDSupported || connecting
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 border border-border-card shrink-0">
                    <HardDrive className="h-6 w-6 text-text-muted" />
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-text-primary">
                      {t("wallet.select.ledger.title")}
                    </p>
                    <p className="text-sm text-text-muted">
                      {t("wallet.select.ledger.desc")}
                    </p>
                  </div>

                  <ChevronRight className="h-5 w-5 text-text-muted shrink-0" />
                </div>
              </AnimatedCard>
            </motion.div>
          ) : (
            <motion.p
              variants={itemVariants}
              className="text-xs text-warning-dark bg-warning/10 border border-warning/30 px-3 py-2 rounded-lg"
            >
              {t("wallet.select.maxLedgerReached", { max: MAX_LEDGER_WALLETS })}
            </motion.p>
          )}

          {canConnectLedger && !webHIDSupported && (
            <motion.p
              variants={itemVariants}
              className="text-xs text-warning-dark bg-warning/10 border border-warning/30 px-3 py-2 rounded-lg"
            >
              {t("wallet.select.webHidUnsupported")}
            </motion.p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
